import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ExpandMore,
  Add,
  Delete,
  Save,
  Send,
  Info,
  AcUnit,
  Thermostat,
  PhotoCamera,
  Draw,
  CheckCircle,
  Person,
  Business,
  Settings,
  ElectricBolt,
  DeviceThermostat,
  Weekend,
  Inventory,
  Science,
  Visibility,
  Air,
  Cancel,
  Build
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SignatureCanvas from '../../components/SignatureCanvas';
import ImageUploader from '../../components/ImageUploader';
import AppHeader from '../../components/AppHeader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// Helper function to capitalize first letter of each word
const capitalizeFirst = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export default function IBNKlimaForm() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Version: 2.0 - Full width layout
  const VERSION = "2.0";
  const customer = location.state?.customer;
  const editFormData = location.state?.formData;
  const templateData = location.state?.templateData;  // NEU: Template-Modus
  const editMode = location.state?.editMode;
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [autoSavedData, setAutoSavedData] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const [error, setError] = useState('');
  const [lastAutoSaveTime, setLastAutoSaveTime] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const [expanded, setExpanded] = useState('panel1');
  
  // Refs für erstes Feld in jedem Accordion
  const panel1FirstFieldRef = useRef(null);
  const panel2FirstFieldRef = useRef(null);
  const panel3FirstFieldRef = useRef(null);
  const panel4FirstFieldRef = useRef(null);
  const panel9FirstFieldRef = useRef(null);
  
  const [herstellerList, setHerstellerList] = useState([
    'Kaysun', 'Mitsubishi', 'Daikin', 'Panasonic', 'LG', 'Samsung', 
    'Toshiba', 'Fujitsu', 'Hitachi', 'Sonstiger'
  ]);
  
  // Signature Dialogs
  const [monteurSigDialog, setMonteurSigDialog] = useState(false);
  const [kundeSigDialog, setKundeSigDialog] = useState(false);

  // Determine data source: template > edit > customer > default
  const sourceData = templateData || editFormData;
  
  // Debug: Beim Laden prüfen, ob Customer-Daten vorhanden sind
  useEffect(() => {
    console.log('🔍 Customer-Daten geladen:', {
      customer: customer,
      ku_nr: customer?.ku_nr,
      name: customer?.name,
      email: customer?.email
    });
    
    if (sourceData) {
      console.log('🔍 sourceData geladen:', {
        id: sourceData._id,
        status: sourceData.status,
        bilderAnzahl: sourceData.bilder?.length || 0,
        bilderVorhanden: !!sourceData.bilder,
        ersteBildKeys: sourceData.bilder?.[0] ? Object.keys(sourceData.bilder[0]) : 'keine'
      });
    }
  }, [sourceData, customer]);

  // Form Data
  const [formData, setFormData] = useState({
    // Allgemeine Informationen
    datum: sourceData?.datum || new Date().toISOString().split('T')[0],
    anlagen_id: sourceData?.anlagen_id || '',
    auftragsnummer: sourceData?.auftragsnummer || '',
    kunde_name: customer?.name || sourceData?.kunde_name || '',
    kundennummer: customer?.ku_nr || sourceData?.kundennummer || sourceData?.ku_nr || '',
    kunde_email: customer?.email || sourceData?.kunde_email || '',
    kunde_strasse: customer?.strasse || sourceData?.kunde_strasse || '',
    kunde_plz: customer?.plz || sourceData?.kunde_plz || '',
    kunde_ort: customer?.ort || sourceData?.kunde_ort || '',
    standort: sourceData?.standort || '',
    hersteller: sourceData?.hersteller || 'Kaysun',  // Standard: Kaysun
    hersteller_sonstiger: sourceData?.hersteller_sonstiger || '',  // NEU
    anzahl_innengeraete: sourceData?.anzahl_innengeraete || 1,
    
    // Innengeräte (Array)
    innengeraete: sourceData?.innengeraete || [{
      montageort: '',  // NEU
      modell: '',
      bauart: 'Wandgerät',  // NEU: Bauart
      steuerung: 'Infrarot',  // NEU: Steuerung
      seriennummer: '',
      betriebsmodus: 'kühlen',  // NEU: kühlen oder heizen
      ansaug_temp: '',
      ausblas_temp: '',
      luefterstufe: '',  // NEU: Lüfterstufe 1-7 oder Automatik
      // IBN: Kondenswasserpumpe pro Innengerät
      kondenswasserpumpe: {
        vorhanden: false,
        hersteller: 'aspen', // default
        typ: '',
        seriennummer: ''
      },
      wifi_modul: false,  // NEU: WiFi-Modul ja/nein
      // IBN: Zusatzmodule
      zusatzmodule: []  // Array: WiFi, KNX, SmartGrid
    }],
    
    // Außengerät (IBN-spezifisch erweitert)
    aussengeraet: {
      montageort: sourceData?.aussengeraet?.montageort || 'boden',
      modell: sourceData?.aussengeraet?.modell || '',
      seriennummer: sourceData?.aussengeraet?.seriennummer || '',
      // aussen_temp ENTFERNT (Anforderung: "Außentemperatur entfernen")
      
      // IBN: Gesamtkälteleistung (hierher verschoben)
      gesamtkaelteleistung: sourceData?.aussengeraet?.gesamtkaelteleistung || sourceData?.gesamtkaelteleistung || '',
      
      // IBN: Elektrische Anschlüsse (1/3-Phasen-Logik)
      phasen: sourceData?.aussengeraet?.phasen || '1', // '1' oder '3'
      // Bei 1-Phase: nur L1
      l1_spannung: sourceData?.aussengeraet?.l1_spannung || '',
      l1_strom: sourceData?.aussengeraet?.l1_strom || '',
      // Bei 3-Phase: L1, L2, L3
      l2_spannung: sourceData?.aussengeraet?.l2_spannung || '',
      l2_strom: sourceData?.aussengeraet?.l2_strom || '',
      l3_spannung: sourceData?.aussengeraet?.l3_spannung || '',
      l3_strom: sourceData?.aussengeraet?.l3_strom || '',
      
      // IBN: Spannungsversorgung & Absicherung (hierher verschoben)
      spannungsversorgung: sourceData?.aussengeraet?.spannungsversorgung || '',
      absicherung: sourceData?.aussengeraet?.absicherung || '',
      
      // IBN: Reparaturschalter
      reparaturschalter_vorhanden: sourceData?.aussengeraet?.reparaturschalter_vorhanden || false,
      reparaturschalter_modell: sourceData?.aussengeraet?.reparaturschalter_modell || '',
      
      // IBN: Zusatzplatine
      zusatzplatine_vorhanden: sourceData?.aussengeraet?.zusatzplatine_vorhanden || false,
      zusatzplatine_modell: sourceData?.aussengeraet?.zusatzplatine_modell || ''
      
      // "arbeiten" ENTFERNT (Anforderung: "durchgeführte Arbeiten entfernen")
    },
    
    // KÄLTEKREISLAUF (Panel 5+6 kombiniert)
    kaeltekreislauf: {
      // Kältemittel
      kaeltemittel: sourceData?.kaeltekreislauf?.kaeltemittel || sourceData?.kaeltemittel || 'R32',
      fuellmenge_typenschild: sourceData?.kaeltekreislauf?.fuellmenge_typenschild || sourceData?.fuellmenge_typenschild || '',
      nachfuellmenge: sourceData?.kaeltekreislauf?.nachfuellmenge || sourceData?.nachfuellmenge || '0',
      gesamtfuellmenge: sourceData?.kaeltekreislauf?.gesamtfuellmenge || sourceData?.gesamtfuellmenge || '',
      
      // IBN-spezifisch: Kältemittelverteiler & Leitungen
      y_kaeltemittelverteiler: sourceData?.kaeltekreislauf?.y_kaeltemittelverteiler || sourceData?.y_kaeltemittelverteiler || 0,
      hoehendifferenz: sourceData?.kaeltekreislauf?.hoehendifferenz || sourceData?.hoehendifferenz || '',
      leitungslaenge: sourceData?.kaeltekreislauf?.leitungslaenge || sourceData?.leitungslaenge || '',
      lotstellen_vorhanden: sourceData?.kaeltekreislauf?.lotstellen_vorhanden || sourceData?.lotstellen_vorhanden || false,
      isolierte_leitungen: sourceData?.kaeltekreislauf?.isolierte_leitungen || sourceData?.isolierte_leitungen || false,
      
      // Dichteprüfung (in gleichen Panel integriert)
      dichtepruefung_durchgefuehrt: sourceData?.kaeltekreislauf?.dichtepruefung_durchgefuehrt || sourceData?.dichtepruefung_durchgefuehrt || false,
      pruefdruck: sourceData?.kaeltekreislauf?.pruefdruck || sourceData?.pruefdruck || '',
      pruefzeit: sourceData?.kaeltekreislauf?.pruefzeit || sourceData?.pruefzeit || '',
      evakuierungszeit: sourceData?.kaeltekreislauf?.evakuierungszeit || sourceData?.evakuierungszeit || '',
      erzeugtes_vakuum: sourceData?.kaeltekreislauf?.erzeugtes_vakuum || sourceData?.erzeugtes_vakuum || '',
      anlage_status: sourceData?.kaeltekreislauf?.anlage_status || sourceData?.anlage_status || 'dicht' // dicht/undicht
    },
    
    // WARTUNG: Dichteprüfung erforderlich (Panel 7, ehemals 8)
    wartung: {
      dichtepruefung_erforderlich: sourceData?.wartung?.dichtepruefung_erforderlich || sourceData?.dichtepruefung_erforderlich || false,
      pruefintervall_frequenz: sourceData?.wartung?.pruefintervall_frequenz || sourceData?.pruefintervall || '12', // Optionen: 6, 12, 24 Monate
      wartungsangebot_gewuenscht: sourceData?.wartung?.wartungsangebot_gewuenscht || sourceData?.wartungsangebot_gewuenscht || false
    },
    
    // Bilder
    bilder: sourceData?.bilder || [],  // NEU: max 5 Bilder
    
    // FUNKTIONSTEST & DOKUMENTATION (Panel 8, ehemals kombiniert)
    funktionstest: {
      testbetrieb_kuehlen: sourceData?.funktionstest?.testbetrieb_kuehlen || sourceData?.testbetrieb_kuehlen || false,
      testbetrieb_heizen: sourceData?.funktionstest?.testbetrieb_heizen || sourceData?.testbetrieb_heizen || false,
      kondenswasser_geprueft: sourceData?.funktionstest?.kondenswasser_geprueft || sourceData?.kondenswasser_geprueft || false,
      inbetriebnahme_durchgefuehrt: sourceData?.funktionstest?.inbetriebnahme_durchgefuehrt || sourceData?.inbetriebnahme_durchgefuehrt || false,
      maengel: sourceData?.funktionstest?.maengel || sourceData?.maengel || '',
      uebergabe_betriebsbuch: sourceData?.funktionstest?.uebergabe_betriebsbuch || sourceData?.uebergabe_betriebsbuch || false,
      einweisung_durchgefuehrt: sourceData?.funktionstest?.einweisung_durchgefuehrt || sourceData?.einweisung_durchgefuehrt || false,
      unterwiesene_personen: sourceData?.funktionstest?.unterwiesene_personen || sourceData?.unterwiesene_personen || '',
      maengel_festgestellt: sourceData?.funktionstest?.maengel_festgestellt || 'nein',  // NEU: ja oder nein
      festgestellte_maengel: sourceData?.funktionstest?.festgestellte_maengel || ''  // NEU
    },
    
    // Hinweise & Unterschriften (Panel 9)
    hinweise: sourceData?.hinweise || '',
    monteur_unterschrift: sourceData?.monteur_unterschrift || '',
    kunde_zufrieden: sourceData?.kunde_zufrieden || 'ja',  // String: 'ja' oder 'nein'
    kunde_zufrieden_begruendung: sourceData?.kunde_zufrieden_begruendung || '',
    kunde_name_unterschrift: sourceData?.kunde_name_unterschrift || '',  // NEU
    kunde_unterschrift: sourceData?.kunde_unterschrift || '',
    
    monteur: sourceData?.monteur || user?.full_name || user?.username || '',
    status: sourceData?.status || 'draft'
  });

  // AUTO-SAVE FUNKTIONEN
  const AUTO_SAVE_KEY = editMode && editFormData?._id 
    ? `ibn_klima_protocol_autosave_edit_${editFormData._id}`
    : 'ibn_klima_protocol_autosave';
  const AUTO_SAVE_EXPIRY = 24 * 60 * 60 * 1000; // 24 Stunden in Millisekunden

  // Beim Laden: Prüfe ob Auto-Save vorhanden
  useEffect(() => {
    const savedData = localStorage.getItem(AUTO_SAVE_KEY);
    const TEMPLATE_SESSION_KEY = 'klima_template_session';
    
    // Template-Modus: Markiere in SessionStorage (überlebt Re-Mounts in gleicher Session)
    if (templateData) {
      const existingSession = sessionStorage.getItem(TEMPLATE_SESSION_KEY);
      
      if (!existingSession) {
        // Erstes Mal in dieser Session - Markiere als Template-Session
        sessionStorage.setItem(TEMPLATE_SESSION_KEY, Date.now().toString());
        
        if (savedData) {
          localStorage.removeItem(AUTO_SAVE_KEY);
          console.log('🔄 Auto-Save: Template geladen, altes Auto-Save gelöscht');
        } else {
          console.log('📋 Template-Modus: Kein altes Auto-Save vorhanden');
        }
      } else {
        console.log('📋 Template-Session läuft bereits, Auto-Save erlaubt');
      }
      return;
    }

    // Kein Template-Modus: Lösche Template-Session-Marker
    sessionStorage.removeItem(TEMPLATE_SESSION_KEY);

    // Prüfe Auto-Save
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        const now = Date.now();
        const age = now - parsed.timestamp;

        // Prüfe ob < 24h alt
        if (age < AUTO_SAVE_EXPIRY) {
          setAutoSavedData(parsed.data);
          setShowRestoreDialog(true);
        } else {
          // Zu alt, lösche Auto-Save
          localStorage.removeItem(AUTO_SAVE_KEY);
        }
      } catch (e) {
        console.error('Auto-Save konnte nicht geladen werden:', e);
        localStorage.removeItem(AUTO_SAVE_KEY);
      }
    }
  }, [AUTO_SAVE_KEY, templateData]);

  // Auto-Save: Speichere Formulardaten (debounced)
  useEffect(() => {
    // Debounce: Warte 3 Sekunden nach letzter Änderung
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    setAutoSaveStatus('Wird gespeichert...');

    autoSaveTimerRef.current = setTimeout(() => {
      // Nur speichern wenn Formulardaten vorhanden
      const hasContent = formData.kunde_name || formData.auftragsnummer || (formData.innengeraete && formData.innengeraete[0]?.modell);
      
      const modus = editMode && editFormData?._id ? `Edit (ID: ${editFormData._id})` : 'Neu';
      console.log(`📋 Auto-Save Check [${modus}]:`, {
        key: AUTO_SAVE_KEY,
        kunde_name: formData.kunde_name,
        auftragsnummer: formData.auftragsnummer,
        hasInnengeraete: !!formData.innengeraete,
        firstModell: formData.innengeraete?.[0]?.modell,
        hasContent
      });

      if (hasContent) {
        try {
          const dataToSave = {
            data: formData,
            timestamp: Date.now()
          };
          localStorage.setItem(AUTO_SAVE_KEY, JSON.stringify(dataToSave));
          const now = new Date();
          const timeStr = now.toLocaleTimeString('de-DE');
          setLastAutoSaveTime(timeStr);
          setAutoSaveStatus(`Gespeichert um ${timeStr}`);
          console.log(`💾 Auto-Save gespeichert [${modus}] um ${timeStr} → Key: ${AUTO_SAVE_KEY}`);
        } catch (e) {
          console.error('❌ Auto-Save Fehler:', e);
          setAutoSaveStatus('Fehler beim Speichern');
        }
      } else {
        console.log('⏭️ Auto-Save: Keine Daten zum Speichern');
        setAutoSaveStatus('');
      }
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [formData, AUTO_SAVE_KEY, editMode, editFormData]);

  // Lösche Auto-Save nach erfolgreichem Submit
  const clearAutoSave = () => {
    localStorage.removeItem(AUTO_SAVE_KEY);
    console.log('🗑️ Auto-Save gelöscht');
  };

  // Stelle Auto-Save wieder her
  const handleRestoreAutoSave = () => {
    if (autoSavedData) {
      setFormData(autoSavedData);
      setShowRestoreDialog(false);
      alert('Ihr begonnenes Formular wurde wiederhergestellt!');
    }
  };

  // Verwerfe Auto-Save
  const handleDiscardAutoSave = () => {
    clearAutoSave();
    setShowRestoreDialog(false);
    setAutoSavedData(null);
  };

  // Handle accordion change
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    
    // Fokussiere das erste Feld des geöffneten Accordions
    if (isExpanded) {
      setTimeout(() => {
        switch(panel) {
          case 'panel1':
            panel1FirstFieldRef.current?.focus();
            break;
          case 'panel2':
            panel2FirstFieldRef.current?.focus();
            break;
          case 'panel3':
            panel3FirstFieldRef.current?.focus();
            break;
          case 'panel4':
            panel4FirstFieldRef.current?.focus();
            break;
          case 'panel9':
            panel9FirstFieldRef.current?.focus();
            break;
          default:
            break;
        }
      }, 300); // Kleine Verzögerung für die Accordion-Animation
    }
  };
  
  // Helper: Capitalize first letter
  const capitalizeFirst = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Handle Bilder
  const handleImagesSelected = (images) => {
    setFormData(prev => ({
      ...prev,
      bilder: images
    }));
  };

  // Handle nested field changes (Außengerät)
  const handleAussengeraetChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      aussengeraet: {
        ...prev.aussengeraet,
        [field]: value
      }
    }));
  };

  // Handle Kältekreislauf changes
  const handleKaeltekreislaufChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      kaeltekreislauf: {
        ...prev.kaeltekreislauf,
        [field]: value
      }
    }));
  };

  // Handle Wartung changes
  const handleWartungChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      wartung: {
        ...prev.wartung,
        [field]: value
      }
    }));
  };

  // Handle Funktionstest changes
  const handleFunktionstestChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      funktionstest: {
        ...prev.funktionstest,
        [field]: value
      }
    }));
  };

  // Handle Kondenswasserpumpe changes für Innengerät
  const handleKondenswasserpumpeChange = (index, field, value) => {
    const newInnengeraete = [...formData.innengeraete];
    newInnengeraete[index] = {
      ...newInnengeraete[index],
      kondenswasserpumpe: {
        ...newInnengeraete[index].kondenswasserpumpe,
        [field]: value
      }
    };
    setFormData(prev => ({
      ...prev,
      innengeraete: newInnengeraete
    }));
  };

  // Handle Zusatzmodule toggle für Innengerät
  const handleZusatzmoduleToggle = (index, modul) => {
    const newInnengeraete = [...formData.innengeraete];
    const currentModules = newInnengeraete[index].zusatzmodule || [];
    
    if (currentModules.includes(modul)) {
      // Remove module
      newInnengeraete[index].zusatzmodule = currentModules.filter(m => m !== modul);
    } else {
      // Add module
      newInnengeraete[index].zusatzmodule = [...currentModules, modul];
    }
    
    setFormData(prev => ({
      ...prev,
      innengeraete: newInnengeraete
    }));
  };

  // Handle Innengerät changes
  const handleInnengeraetChange = (index, field, value) => {
    const newInnengeraete = [...formData.innengeraete];
    newInnengeraete[index] = {
      ...newInnengeraete[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      innengeraete: newInnengeraete
    }));
  };

  // Prüfe ob Temperaturen realistisch sind
  const checkTemperatureLogic = (geraet) => {
    const ansaug = parseFloat(geraet.ansaug_temp);
    const ausblas = parseFloat(geraet.ausblas_temp);
    const betriebsmodus = geraet.betriebsmodus || 'kühlen';
    
    if (!ansaug || !ausblas) {
      return null;
    }
    
    if (betriebsmodus === 'kühlen') {
      // Beim Kühlen muss Ausblas < Ansaug sein
      if (ausblas >= ansaug) {
        return 'Im Kühlbetrieb muss die Ausblastemperatur niedriger sein als die Ansaugtemperatur!';
      }
    } else {
      // Beim Heizen muss Ausblas > Ansaug sein
      if (ausblas <= ansaug) {
        return 'Im Heizbetrieb muss die Ausblastemperatur höher sein als die Ansaugtemperatur!';
      }
    }
    
    return null;
  };

  // Berechne Effizienzeinschätzung basierend auf Temperaturdifferenz
  const calculateEfficiency = (geraet) => {
    const ansaug = parseFloat(geraet.ansaug_temp);
    const ausblas = parseFloat(geraet.ausblas_temp);
    const luefterstufe = geraet.luefterstufe;
    
    // Prüfe ob alle Werte vorhanden sind
    if (!ansaug || !ausblas || !luefterstufe) {
      return null;
    }
    
    // Prüfe ob höchste Lüfterstufe verwendet wird
    const hoechsteStufen = ['Stufe 3', 'Stufe 4', 'Stufe 5', 'Stufe 7', 'Automatik'];
    if (!hoechsteStufen.includes(luefterstufe)) {
      return null; // Keine Bewertung bei niedrigen Stufen
    }
    
    // Berechne Temperaturdifferenz (absolut)
    const deltaT = Math.abs(ausblas - ansaug);
    const betriebsmodus = geraet.betriebsmodus || 'kühlen';
    
    let status = 'gut';
    let color = '#4caf50'; // Grün
    let sollbereich = '';
    
    if (betriebsmodus === 'kühlen') {
      sollbereich = '6-9 K';
      if (deltaT > 9) {
        status = 'sehr gut';
        color = '#4caf50';
      } else if (deltaT >= 6) {
        status = 'gut';
        color = '#4caf50';
      } else if (deltaT >= 4) {
        status = 'ausreichend';
        color = '#ff9800'; // Orange
      } else {
        status = 'mangelhaft';
        color = '#f44336'; // Rot
      }
    } else { // heizen
      sollbereich = '8-12 K';
      if (deltaT > 12) {
        status = 'sehr gut';
        color = '#4caf50';
      } else if (deltaT >= 8) {
        status = 'gut';
        color = '#4caf50';
      } else if (deltaT >= 5) {
        status = 'ausreichend';
        color = '#ff9800';
      } else {
        status = 'mangelhaft';
        color = '#f44336';
      }
    }
    
    return {
      status,
      color,
      deltaT: deltaT.toFixed(1),
      sollbereich,
      betriebsmodus
    };
  };

  // Format Auftragsnummer: XXXX/YY (z.B. 0001/26, 0028/26, 0321/26, 1234/26)
  // Formatierung erfolgt beim Verlassen des Feldes (onBlur)
  const handleAuftragsnummerChange = (value) => {
    // Entferne alles außer Zahlen und /
    const cleaned = value.replace(/[^\d/]/g, '');
    setFormData(prev => ({ ...prev, auftragsnummer: cleaned }));
  };

  // Formatierung beim Verlassen des Feldes
  const handleAuftragsnummerBlur = () => {
    const value = formData.auftragsnummer;
    // Extrahiere nur Zahlen
    const numbers = value.replace(/[^\d]/g, '');
    
    if (numbers.length === 0) {
      return;
    }
    
    // Begrenze auf maximal 4 Ziffern
    const limitedNumbers = numbers.slice(0, 4);
    
    // Formatierung: Immer 4 Stellen mit führenden Nullen
    // 1 → 0001/26, 28 → 0028/26, 321 → 0321/26, 1234 → 1234/26
    if (limitedNumbers.length >= 1) {
      const currentYear = new Date().getFullYear().toString().slice(-2);
      // Fülle mit führenden Nullen auf 4 Stellen auf
      const paddedNumbers = limitedNumbers.padStart(4, '0');
      const formatted = `${paddedNumbers}/${currentYear}`;
      setFormData(prev => ({ ...prev, auftragsnummer: formatted }));
    }
  };

  // Handle Enter key for checkboxes
  const handleCheckboxKeyDown = (e, onToggle) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onToggle();
    }
  };

  // Add new Innengerät
  const handleAddInnengeraet = () => {
    if (formData.innengeraete.length >= 20) {
      alert('Maximal 20 Innengeräte erlaubt');
      return;
    }
    setFormData(prev => ({
      ...prev,
      innengeraete: [...prev.innengeraete, {
        montageort: '',
        modell: '',
        seriennummer: '',
        betriebsmodus: 'kühlen',  // Standard Kühlbetrieb
        ansaug_temp: '',
        ausblas_temp: '',
        luefterstufe: '',  // Lüfterstufe 1-7 oder Automatik
        // IBN: Kondenswasserpumpe
        kondenswasserpumpe: {
          vorhanden: false,
          hersteller: 'aspen', // default
          typ: '',
          seriennummer: ''
        },
        wifi_modul: false,  // NEU: WiFi-Modul ja/nein
        // IBN: Zusatzmodule
        zusatzmodule: []  // Array: WiFi, KNX, SmartGrid
      }]
    }));
    
    // Auto-open neues Accordion und setze Focus auf Montageort
    setTimeout(() => {
      setExpanded('panel2');
      const newIndex = formData.innengeraete.length;
      const montageortInput = document.getElementById(`montageort-${newIndex}`);
      if (montageortInput) {
        montageortInput.focus();
      }
    }, 100);
  };

  // Remove Innengerät
  const handleRemoveInnengeraet = (index) => {
    if (formData.innengeraete.length === 1) {
      alert('Mindestens 1 Innengerät erforderlich');
      return;
    }
    const newInnengeraete = formData.innengeraete.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      innengeraete: newInnengeraete
    }));
  };

  // Calculate Gesamtfüllmenge
  useEffect(() => {
    const typenschild = parseFloat(formData.fuellmenge_typenschild) || 0;
    const nachfuell = parseFloat(formData.nachfuellmenge) || 0;
    const gesamt = (typenschild + nachfuell).toFixed(2);
    setFormData(prev => ({
      ...prev,
      gesamtfuellmenge: gesamt
    }));
  }, [formData.fuellmenge_typenschild, formData.nachfuellmenge]);

  // Update Anzahl Innengeräte when array changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      anzahl_innengeraete: prev.innengeraete.length
    }));
  }, [formData.innengeraete.length]);

  // Handle Save Draft
  // Handle Submit
  const handleSubmit = async () => {
    // Validation
    if (!formData.kunde_name) {
      setError('Bitte Kundenname eingeben');
      setExpanded('panel1');
      return;
    }
    if (!formData.auftragsnummer) {
      setError('Bitte Auftragsnummer eingeben');
      setExpanded('panel1');
      return;
    }
    if (!formData.monteur_unterschrift) {
      setError('Bitte Monteur-Unterschrift eintragen');
      setExpanded('panel9');
      return;
    }
    if (!formData.kunde_unterschrift) {
      setError('Bitte Kunden-Unterschrift eintragen');
      setExpanded('panel9');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      
      // Berechne Effizienzlabels für alle Innengeräte
      const innengeraeteWithEfficiency = formData.innengeraete.map(geraet => {
        const efficiency = calculateEfficiency(geraet);
        return {
          ...geraet,
          effizienz_status: efficiency?.status || null,
          effizienz_delta_t: efficiency?.deltaT || null
        };
      });
      
      const payload = {
        ...formData,
        innengeraete: innengeraeteWithEfficiency,
        status: 'submitted'
      };

      let response;
      if (editMode && editFormData?._id) {
        response = await axios.put(
          `${API_URL}/ibn-klima-protocols/${editFormData._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          `${API_URL}/ibn-klima-protocols`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      clearAutoSave(); // Lösche Auto-Save nach erfolgreichem Absenden
      alert('Wartungsprotokoll erfolgreich versendet!');
      navigate('/protocol/ibn-klima-list');
    } catch (err) {
      console.error('Error submitting protocol:', err);
      setError('Fehler beim Versenden: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      
      // Berechne Effizienzlabels für alle Innengeräte
      const innengeraeteWithEfficiency = formData.innengeraete.map(geraet => {
        const efficiency = calculateEfficiency(geraet);
        return {
          ...geraet,
          effizienz_status: efficiency?.status || null,
          effizienz_delta_t: efficiency?.deltaT || null
        };
      });
      
      const payload = {
        ...formData,
        innengeraete: innengeraeteWithEfficiency,
        status: 'draft'
      };
      
      console.log('💾 Speichere Entwurf mit', formData.bilder?.length || 0, 'Bildern');

      let response;
      if (editMode && editFormData?._id) {
        response = await axios.put(
          `${API_URL}/ibn-klima-protocols/${editFormData._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          `${API_URL}/ibn-klima-protocols`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      clearAutoSave(); // Lösche Auto-Save nach erfolgreichem Speichern
      alert('Entwurf erfolgreich gespeichert!');
      navigate('/protocol/ibn-klima-list');
    } catch (err) {
      console.error('Error saving draft:', err);
      setError('Fehler beim Speichern: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      
      // Berechne Effizienzlabels für alle Innengeräte (wie bei Submit)
      const innengeraeteWithEfficiency = formData.innengeraete.map(geraet => {
        const efficiency = calculateEfficiency(geraet);
        return {
          ...geraet,
          effizienz_status: efficiency?.status || null,
          effizienz_delta_t: efficiency?.deltaT || null
        };
      });
      
      const payload = {
        ...formData,
        innengeraete: innengeraeteWithEfficiency,
        status: 'draft'
      };

      // Call IBN Klima preview endpoint
      const response = await axios.post(
        `${API_URL}/ibn-klima-protocols/preview`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.pdf_id) {
        // Open PDF in new tab with token as query parameter
        const pdfUrl = `${API_URL}/ibn-klima-protocols/pdf/${response.data.pdf_id}?token=${token}`;
        window.open(pdfUrl, '_blank');
      }
    } catch (err) {
      console.error('Error generating preview:', err);
      setError('Fehler bei PDF-Vorschau: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <AppHeader title="Wartungsprotokoll Klimaanlage" showBack={true} backTo="/protocol/customer-search" />
      
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Formular-Überschrift */}
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            mb: 3, 
            fontWeight: 'bold', 
            color: '#1976d2',
            textAlign: 'center',
            borderBottom: '3px solid #1976d2',
            pb: 1
          }}
        >
          Inbetriebnahme Klimaanlage
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Auto-Save Status Indikator */}
        {autoSaveStatus && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 2,
              py: 0.5,
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }
            }}
            icon={<span>💾</span>}
          >
            <strong>Auto-Speicherung{editMode ? ' (Entwurf)' : ''}:</strong> {autoSaveStatus}
          </Alert>
        )}

        {/* Accordion 1: Allgemeine Informationen */}
        <Accordion 
          expanded={expanded === 'panel1'} 
          onChange={handleAccordionChange('panel1')}
          sx={{ 
            mb: 2,
            boxShadow: 3,
            '&:before': { display: 'none' },
            borderRadius: '8px !important'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: '#1976d2',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&:hover': { backgroundColor: '#1565c0' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>1. Allgemeine Informationen</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* === PROTOKOLL-INFO CARD === */}
              <Card sx={{ backgroundColor: '#e3f2fd', border: '1px solid #1976d2' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    📋 Protokoll-Informationen
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Datum"
                      type="date"
                      value={formData.datum}
                      onChange={(e) => handleChange('datum', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputRef={panel1FirstFieldRef}
                    />
                    
                    <TextField
                      fullWidth
                      label="Anlagen-ID"
                      value={formData.anlagen_id}
                      onChange={(e) => handleChange('anlagen_id', e.target.value)}
                    />
                    
                    <TextField
                      required
                      fullWidth
                      label="Auftragsnummer"
                      value={formData.auftragsnummer}
                      onChange={(e) => handleAuftragsnummerChange(e.target.value)}
                      onBlur={handleAuftragsnummerBlur}
                      placeholder="z.B. 321"
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#FFE5B4',
                          '&:hover': {
                            backgroundColor: '#FFD699'
                          },
                          '&.Mui-focused': {
                            backgroundColor: '#FFEDCC'
                          }
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
              
              {/* === KUNDENDATEN CARD === */}
              <Card sx={{ backgroundColor: '#f3e5f5', border: '1px solid #9c27b0' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    👤 Kundendaten
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Kundenname"
                      value={formData.kunde_name}
                      onChange={(e) => handleChange('kunde_name', capitalizeFirst(e.target.value))}
                      required
                    />
                    
                    <TextField
                      fullWidth
                      label="Kundennummer"
                      value={formData.kundennummer}
                      onChange={(e) => handleChange('kundennummer', e.target.value)}
                      disabled={!!customer || (editMode && !!formData.kundennummer)}
                      helperText={
                        customer 
                          ? "Von Kundensuche übernommen" 
                          : (editMode && formData.kundennummer) 
                            ? "Kundennummer kann nicht geändert werden"
                            : ""
                      }
                    />
                    
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>Adresse</Typography>
                    
                    <TextField
                      fullWidth
                      label="Straße & Hausnummer"
                      value={formData.kunde_strasse}
                      onChange={(e) => handleChange('kunde_strasse', capitalizeFirst(e.target.value))}
                      disabled={!!customer}
                      helperText={customer ? "Von Kundensuche übernommen" : ""}
                    />
                    
                    <TextField
                      fullWidth
                      label="PLZ"
                      value={formData.kunde_plz}
                      onChange={(e) => handleChange('kunde_plz', e.target.value)}
                      disabled={!!customer}
                      helperText={customer ? "Von Kundensuche übernommen" : ""}
                    />
                    
                    <TextField
                      fullWidth
                      label="Ort"
                      value={formData.kunde_ort}
                      onChange={(e) => handleChange('kunde_ort', capitalizeFirst(e.target.value))}
                      disabled={!!customer}
                      helperText={customer ? "Von Kundensuche übernommen" : ""}
                    />
                    
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                  label="Standort der Anlage"
                  value={formData.standort}
                  onChange={(e) => handleChange('standort', capitalizeFirst(e.target.value))}
                />
              </Box>
            </CardContent>
          </Card>
          
          {/* === ANLAGENDATEN CARD === */}
          <Card sx={{ backgroundColor: '#fff3e0', border: '1px solid #ff9800' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                🏭 Anlagendaten
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Hersteller</InputLabel>
                  <Select
                    value={formData.hersteller}
                    label="Hersteller"
                    onChange={(e) => handleChange('hersteller', e.target.value)}
                  >
                    {herstellerList.map((h) => (
                      <MenuItem key={h} value={h}>{h}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Sonstiger Hersteller Textfeld */}
                {formData.hersteller === 'Sonstiger' && (
                  <TextField
                    fullWidth
                    label="Hersteller (Sonstiger)"
                    value={formData.hersteller_sonstiger}
                    onChange={(e) => handleChange('hersteller_sonstiger', e.target.value.toUpperCase())}
                  />
                )}
                
                <TextField
                  fullWidth
                  label="Anzahl Innengeräte"
                  type="number"
                  value={formData.innengeraete.length}
                  disabled
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </CardContent>
          </Card>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 2: Innengeräte */}
        <Accordion 
          expanded={expanded === 'panel2'} 
          onChange={handleAccordionChange('panel2')}
          sx={{ 
            mb: 2,
            boxShadow: 3,
            '&:before': { display: 'none' },
            borderRadius: '8px !important'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: '#2e7d32',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&:hover': { backgroundColor: '#1b5e20' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AcUnit />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>2. Innengeräte ({formData.innengeraete.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Box>
              {formData.innengeraete.map((geraet, index) => (
                <Card 
                  key={index} 
                  sx={{ 
                    mb: 3, 
                    border: '2px solid #2e7d32',
                    borderRadius: 2,
                    boxShadow: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      boxShadow: 4,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      mb: 3,
                      pb: 2,
                      borderBottom: '2px solid #e0e0e0'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AcUnit sx={{ color: '#2e7d32' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                          Innengerät #{index + 1}
                        </Typography>
                      </Box>
                      {formData.innengeraete.length > 1 && (
                        <IconButton 
                          color="error" 
                          onClick={() => handleRemoveInnengeraet(index)}
                          size="small"
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: '#ffebee',
                              transform: 'scale(1.1)'
                            }
                          }}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      
                      {/* === GRUNDDATEN BLOCK === */}
                      <Card sx={{ backgroundColor: '#f5f9ff', border: '1px solid #1976d2' }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            📋 Grunddaten
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                              id={`montageort-${index}`}
                              fullWidth
                              label="Montageort (z.B. Schlafzimmer, Büro, Küche)"
                              value={geraet.montageort || ''}
                              onChange={(e) => handleInnengeraetChange(index, 'montageort', capitalizeFirst(e.target.value))}
                              placeholder="Schlafzimmer"
                              inputRef={index === 0 ? panel2FirstFieldRef : null}
                            />
                            
                            <TextField
                              fullWidth
                              label="Modellnummer"
                              value={geraet.modell}
                              onChange={(e) => handleInnengeraetChange(index, 'modell', e.target.value.toUpperCase())}
                            />
                            
                            <FormControl fullWidth>
                              <InputLabel>Bauart</InputLabel>
                              <Select
                                value={geraet.bauart || 'Wandgerät'}
                                onChange={(e) => handleInnengeraetChange(index, 'bauart', e.target.value)}
                                label="Bauart"
                              >
                                <MenuItem value="Wandgerät">Wandgerät</MenuItem>
                                <MenuItem value="Truhengerät">Truhengerät</MenuItem>
                                <MenuItem value="Kassettengerät">Kassettengerät</MenuItem>
                                <MenuItem value="Kanalgerät">Kanalgerät</MenuItem>
                                <MenuItem value="Unterdeckengerät">Unterdeckengerät</MenuItem>
                                <MenuItem value="Standgerät">Standgerät</MenuItem>
                                <MenuItem value="Hydrobox">Hydrobox</MenuItem>
                                <MenuItem value="DHW Tank">DHW Tank</MenuItem>
                                <MenuItem value="AHU Lüftung">AHU Lüftung</MenuItem>
                                <MenuItem value="Sonstiges">Sonstiges</MenuItem>
                              </Select>
                            </FormControl>
                            
                            <FormControl fullWidth>
                              <InputLabel>Steuerung</InputLabel>
                              <Select
                                value={geraet.steuerung || 'Infrarot'}
                                onChange={(e) => handleInnengeraetChange(index, 'steuerung', e.target.value)}
                                label="Steuerung"
                              >
                                <MenuItem value="Infrarot">Infrarot</MenuItem>
                                <MenuItem value="Kabel">Kabel</MenuItem>
                                <MenuItem value="Beides">Beides</MenuItem>
                                <MenuItem value="Sonstiges">Sonstiges</MenuItem>
                              </Select>
                            </FormControl>
                            
                            <TextField
                              fullWidth
                              label="Seriennummer"
                              value={geraet.seriennummer}
                              onChange={(e) => handleInnengeraetChange(index, 'seriennummer', e.target.value.toUpperCase())}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                      
                      {/* === MESSWERTE PROBELAUF BLOCK === */}
                      <Card sx={{ backgroundColor: '#fff8f0', border: '1px solid #ff9800' }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Thermostat sx={{ fontSize: 24 }} />
                            Messwerte Probelauf
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                              <FormLabel sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>Betriebsmodus</FormLabel>
                              <RadioGroup
                                row
                                value={geraet.betriebsmodus || 'kühlen'}
                                onChange={(e) => handleInnengeraetChange(index, 'betriebsmodus', e.target.value)}
                              >
                                <FormControlLabel 
                                  value="kühlen" 
                                  control={<Radio />} 
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <AcUnit sx={{ color: '#2196f3', fontSize: 20 }} /> 
                                      Kühlbetrieb
                                    </Box>
                                  } 
                                />
                                <FormControlLabel 
                                  value="heizen" 
                                  control={<Radio />} 
                                  label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Thermostat sx={{ color: '#ff9800', fontSize: 20 }} /> 
                                      Heizbetrieb
                                    </Box>
                                  } 
                                />
                              </RadioGroup>
                            </Box>
                            
                            <Divider />
                            
                            <TextField
                              fullWidth
                              label="Ansaugtemperatur (°C)"
                              type="number"
                              value={geraet.ansaug_temp}
                              onChange={(e) => handleInnengeraetChange(index, 'ansaug_temp', e.target.value)}
                            />
                            
                            <TextField
                              fullWidth
                              label="Ausblastemperatur (°C)"
                              type="number"
                              value={geraet.ausblas_temp}
                              onChange={(e) => handleInnengeraetChange(index, 'ausblas_temp', e.target.value)}
                            />
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Air sx={{ color: '#1976d2', fontSize: 24 }} />
                              <FormControl fullWidth>
                                <InputLabel>Lüfterstufe im Probelauf</InputLabel>
                                <Select
                                  value={geraet.luefterstufe || ''}
                                  onChange={(e) => handleInnengeraetChange(index, 'luefterstufe', e.target.value)}
                                  label="Lüfterstufe im Probelauf"
                                >
                                  <MenuItem value="">-- Keine Angabe --</MenuItem>
                                  <MenuItem value="Stufe 1">Stufe 1</MenuItem>
                                  <MenuItem value="Stufe 2">Stufe 2</MenuItem>
                                  <MenuItem value="Stufe 3">Stufe 3</MenuItem>
                                  <MenuItem value="Stufe 4">Stufe 4</MenuItem>
                                  <MenuItem value="Stufe 5">Stufe 5</MenuItem>
                                  <MenuItem value="Stufe 6">Stufe 6</MenuItem>
                                  <MenuItem value="Stufe 7">Stufe 7</MenuItem>
                                  <MenuItem value="Automatik">Automatik</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                            
                            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                              ⚠️ Bitte höchste Lüfterstufe für Prüfung verwenden (3, 4, 5, 7 oder Automatik)
                            </Alert>
                          </Box>
                        </CardContent>
                      </Card>
                      
                      {/* Fehlervalidierung für unrealistische Temperaturen */}
                      {(() => {
                        const tempError = checkTemperatureLogic(geraet);
                        if (!tempError) return null;
                        
                        return (
                          <Alert severity="error" sx={{ mt: 2, border: '2px solid #f44336', fontWeight: 'bold' }}>
                            ⚠️ Werte überprüfen: {tempError}
                          </Alert>
                        );
                      })()}
                      
                      {/* Effizienzeinschätzung Label */}
                      {(() => {
                        const efficiency = calculateEfficiency(geraet);
                        if (!efficiency) return null;
                        
                        return (
                          <Card sx={{ 
                            backgroundColor: '#f5f5f5', 
                            border: `2px solid ${efficiency.color}`,
                            mt: 2
                          }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <CheckCircle sx={{ color: efficiency.color, fontSize: 28 }} />
                                <Typography variant="h6" component="div">
                                  Effizienzeinschätzung bei Probelauf
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Typography variant="body1">
                                  ΔT: <strong>{efficiency.deltaT} K</strong>
                                </Typography>
                                <Box sx={{
                                  backgroundColor: efficiency.color,
                                  color: 'white',
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: '16px',
                                  fontWeight: 'bold',
                                  textTransform: 'uppercase'
                                }}>
                                  {efficiency.status}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  Sollbereich {efficiency.betriebsmodus === 'kühlen' ? 'Kühlen' : 'Heizen'}: {efficiency.sollbereich}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                💡 Grober Schätzwert basierend auf Temperaturdifferenz bei höchster Lüfterstufe
                              </Typography>
                            </CardContent>
                          </Card>
                        );
                      })()}
                      
                      {/* === KONDENSWASSERPUMPE BLOCK === */}
                      <Card sx={{ backgroundColor: '#f0f8ff', border: '1px solid #1976d2' }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Weekend sx={{ fontSize: 24 }} />
                            Kondenswasserpumpe
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={geraet.kondenswasserpumpe?.vorhanden || false}
                                  onChange={(e) => handleKondenswasserpumpeChange(index, 'vorhanden', e.target.checked)}
                                  onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleKondenswasserpumpeChange(index, 'vorhanden', !geraet.kondenswasserpumpe?.vorhanden))}
                                  sx={{
                                    color: geraet.kondenswasserpumpe?.vorhanden ? 'success.main' : 'default',
                                    '&.Mui-checked': { color: 'success.main' }
                                  }}
                                />
                              }
                              label="Kondenswasserpumpe vorhanden?"
                            />
                            
                            {geraet.kondenswasserpumpe?.vorhanden && (
                              <>
                                <Divider />
                                
                                <FormControl fullWidth>
                                  <InputLabel>Hersteller</InputLabel>
                                  <Select
                                    value={geraet.kondenswasserpumpe?.hersteller || 'aspen'}
                                    onChange={(e) => handleKondenswasserpumpeChange(index, 'hersteller', e.target.value)}
                                    label="Hersteller"
                                  >
                                    <MenuItem value="aspen">Aspen</MenuItem>
                                    <MenuItem value="refco">Refco</MenuItem>
                                    <MenuItem value="sauermann">Sauermann</MenuItem>
                                    <MenuItem value="sonstiger">Sonstiger</MenuItem>
                                  </Select>
                                </FormControl>
                                
                                <TextField
                                  fullWidth
                                  label="Typ / Modell"
                                  value={geraet.kondenswasserpumpe?.typ || ''}
                                  onChange={(e) => handleKondenswasserpumpeChange(index, 'typ', e.target.value)}
                                  placeholder="z.B. Mini Lime, Ultra Silent"
                                />
                                
                                <TextField
                                  fullWidth
                                  label="Seriennummer"
                                  value={geraet.kondenswasserpumpe?.seriennummer || ''}
                                  onChange={(e) => handleKondenswasserpumpeChange(index, 'seriennummer', e.target.value.toUpperCase())}
                                  placeholder="Seriennummer"
                                />
                              </>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                      
                      {/* === ZUSATZMODULE BLOCK === */}
                      <Card sx={{ backgroundColor: '#f9f0ff', border: '1px solid #9c27b0' }}>
                        <CardContent>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Settings sx={{ fontSize: 24 }} />
                            Zusatzmodule
                          </Typography>
                          
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                              <FormLabel sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>WiFi-Modul</FormLabel>
                              <RadioGroup
                                row
                                value={geraet.wifi_modul ? 'ja' : 'nein'}
                                onChange={(e) => handleInnengeraetChange(index, 'wifi_modul', e.target.value === 'ja')}
                              >
                                <FormControlLabel value="ja" control={<Radio />} label="Ja" />
                                <FormControlLabel value="nein" control={<Radio />} label="Nein" />
                              </RadioGroup>
                            </Box>
                            
                            <Divider />
                            
                            <Box>
                              <FormLabel sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>Weitere Zusatzmodule</FormLabel>
                              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {['KNX', 'SmartGrid'].map((modul) => (
                                  <FormControlLabel
                                    key={modul}
                                    control={
                                      <Checkbox
                                        checked={(geraet.zusatzmodule || []).includes(modul)}
                                        onChange={() => handleZusatzmoduleToggle(index, modul)}
                                        onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleZusatzmoduleToggle(index, modul))}
                                        sx={{
                                          color: (geraet.zusatzmodule || []).includes(modul) ? 'success.main' : 'default',
                                          '&.Mui-checked': { color: 'success.main' }
                                        }}
                                      />
                                    }
                                    label={modul}
                                  />
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  </CardContent>
                </Card>
              ))}
              
              {formData.innengeraete.length < 20 && (
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddInnengeraet}
                  fullWidth
                >
                  Innengerät hinzufügen ({formData.innengeraete.length}/20)
                </Button>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 3: Außengerät */}
        <Accordion 
          expanded={expanded === 'panel3'} 
          onChange={handleAccordionChange('panel3')}
          sx={{ 
            mb: 2,
            boxShadow: 3,
            '&:before': { display: 'none' },
            borderRadius: '8px !important'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: '#ed6c02',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&:hover': { backgroundColor: '#e65100' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DeviceThermostat />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>3. Außengerät</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* === GRUNDDATEN AUSSSENGERÄT CARD === */}
              <Card sx={{ backgroundColor: '#fff3e0', border: '1px solid #ed6c02' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#ed6c02', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    🏭 Grunddaten Außengerät
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Montageort Außengerät</InputLabel>
                      <Select
                        value={formData.aussengeraet.montageort || 'boden'}
                        onChange={(e) => handleAussengeraetChange('montageort', e.target.value)}
                        label="Montageort Außengerät"
                        inputRef={panel3FirstFieldRef}
                      >
                        <MenuItem value="boden">Boden</MenuItem>
                        <MenuItem value="wand">Wand</MenuItem>
                        <MenuItem value="dach">Dach</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <TextField
                      fullWidth
                      label="Modellnummer"
                      value={formData.aussengeraet.modell}
                      onChange={(e) => handleAussengeraetChange('modell', e.target.value.toUpperCase())}
                    />
                    
                    <TextField
                      fullWidth
                      label="Seriennummer"
                      value={formData.aussengeraet.seriennummer}
                      onChange={(e) => handleAussengeraetChange('seriennummer', e.target.value.toUpperCase())}
                    />
                    
                    <TextField
                      fullWidth
                      label="Gesamtkälteleistung (kW)"
                      type="number"
                      value={formData.aussengeraet.gesamtkaelteleistung}
                      onChange={(e) => handleAussengeraetChange('gesamtkaelteleistung', e.target.value)}
                      placeholder="z.B. 14.0"
                    />
                  </Box>
                </CardContent>
              </Card>
              
              {/* === ELEKTRISCHE MESSWERTE CARD === */}
              <Card sx={{ backgroundColor: '#fff8e1', border: '1px solid #ff9800' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ElectricBolt sx={{ fontSize: 24 }} />
                    Elektrische Messwerte - Probelauf
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Phasen</InputLabel>
                      <Select
                        value={formData.aussengeraet.phasen || '1'}
                        onChange={(e) => handleAussengeraetChange('phasen', e.target.value)}
                        label="Phasen"
                      >
                        <MenuItem value="1">1-Phasen (230V)</MenuItem>
                        <MenuItem value="3">3-Phasen (400V)</MenuItem>
                      </Select>
                    </FormControl>
                    
                    
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 1 }}>Phase L1</Typography>
                    
                    <TextField
                      fullWidth
                      label="Spannung L1 (V)"
                      type="number"
                      value={formData.aussengeraet.l1_spannung}
                      onChange={(e) => handleAussengeraetChange('l1_spannung', e.target.value)}
                      placeholder="230"
                    />
                    
                    <TextField
                      fullWidth
                      label="Strom L1 (A)"
                      type="number"
                      value={formData.aussengeraet.l1_strom}
                      onChange={(e) => handleAussengeraetChange('l1_strom', e.target.value)}
                      placeholder="5.2"
                    />
                    
                    {/* L2 & L3 - Nur bei 3-Phasen */}
                    {formData.aussengeraet.phasen === '3' && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 1 }}>Phase L2</Typography>
                        
                        <TextField
                          fullWidth
                          label="Spannung L2 (V)"
                          type="number"
                          value={formData.aussengeraet.l2_spannung}
                          onChange={(e) => handleAussengeraetChange('l2_spannung', e.target.value)}
                          placeholder="230"
                        />
                        
                        <TextField
                          fullWidth
                          label="Strom L2 (A)"
                          type="number"
                          value={formData.aussengeraet.l2_strom}
                          onChange={(e) => handleAussengeraetChange('l2_strom', e.target.value)}
                          placeholder="5.2"
                        />
                        
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 1 }}>Phase L3</Typography>
                        
                        <TextField
                          fullWidth
                          label="Spannung L3 (V)"
                          type="number"
                          value={formData.aussengeraet.l3_spannung}
                          onChange={(e) => handleAussengeraetChange('l3_spannung', e.target.value)}
                          placeholder="230"
                        />
                        
                        <TextField
                          fullWidth
                          label="Strom L3 (A)"
                          type="number"
                          value={formData.aussengeraet.l3_strom}
                          onChange={(e) => handleAussengeraetChange('l3_strom', e.target.value)}
                          placeholder="5.2"
                        />
                      </>
                    )}
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <FormControl fullWidth>
                      <InputLabel>Absicherung</InputLabel>
                      <Select
                        value={formData.aussengeraet.absicherung || '16A'}
                        onChange={(e) => handleAussengeraetChange('absicherung', e.target.value)}
                        label="Absicherung"
                      >
                        <MenuItem value="10A">10A</MenuItem>
                        <MenuItem value="16A">16A</MenuItem>
                        <MenuItem value="20A">20A</MenuItem>
                        <MenuItem value="25A">25A</MenuItem>
                        <MenuItem value="32A">32A</MenuItem>
                        <MenuItem value="35A">35A</MenuItem>
                        <MenuItem value="40A">40A</MenuItem>
                        <MenuItem value="50A">50A</MenuItem>
                        <MenuItem value="63A">63A</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </CardContent>
              </Card>
              
              {/* === ZUSÄTZLICHE KOMPONENTEN CARD === */}
              <Card sx={{ backgroundColor: '#f3e5f5', border: '1px solid #9c27b0' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Build sx={{ fontSize: 24 }} />
                    Zusätzliche Komponenten
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.aussengeraet.reparaturschalter_vorhanden || false}
                          onChange={(e) => handleAussengeraetChange('reparaturschalter_vorhanden', e.target.checked)}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetChange('reparaturschalter_vorhanden', !formData.aussengeraet.reparaturschalter_vorhanden))}
                          sx={{
                            color: formData.aussengeraet.reparaturschalter_vorhanden ? 'success.main' : 'default',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label="Reparaturschalter vorhanden?"
                    />
                    
                    {formData.aussengeraet.reparaturschalter_vorhanden && (
                      <TextField
                        fullWidth
                        label="Reparaturschalter Modell"
                        value={formData.aussengeraet.reparaturschalter_modell}
                        onChange={(e) => handleAussengeraetChange('reparaturschalter_modell', e.target.value)}
                        placeholder="Modellbezeichnung"
                      />
                    )}
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.aussengeraet.zusatzplatine_vorhanden || false}
                          onChange={(e) => handleAussengeraetChange('zusatzplatine_vorhanden', e.target.checked)}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleAussengeraetChange('zusatzplatine_vorhanden', !formData.aussengeraet.zusatzplatine_vorhanden))}
                          sx={{
                            color: formData.aussengeraet.zusatzplatine_vorhanden ? 'success.main' : 'default',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label="Zusatzplatine vorhanden?"
                    />
                    
                    {formData.aussengeraet.zusatzplatine_vorhanden && (
                      <TextField
                        fullWidth
                        label="Zusatzplatine Modell"
                        value={formData.aussengeraet.zusatzplatine_modell}
                        onChange={(e) => handleAussengeraetChange('zusatzplatine_modell', e.target.value)}
                        placeholder="Modellbezeichnung"
                      />
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 4: Kältekreislauf und Dichteprüfung */}
        <Accordion 
          expanded={expanded === 'panel4'} 
          onChange={handleAccordionChange('panel4')}
          sx={{ 
            mb: 2,
            boxShadow: 3,
            '&:before': { display: 'none' },
            borderRadius: '8px !important'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: '#9c27b0',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&:hover': { backgroundColor: '#7b1fa2' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Science />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>4. Kältekreislauf und Dichteprüfung</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* === KÄLTEMITTEL CARD === */}
              <Card sx={{ backgroundColor: '#f3e5f5', border: '1px solid #9c27b0' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    ❄️ Kältemittel
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControl fullWidth>
                      <InputLabel>Kältemittel</InputLabel>
                      <Select
                        value={formData.kaeltekreislauf?.kaeltemittel || 'R32'}
                        onChange={(e) => handleKaeltekreislaufChange('kaeltemittel', e.target.value)}
                        label="Kältemittel"
                        inputRef={panel4FirstFieldRef}
                      >
                        <MenuItem value="R32">R32</MenuItem>
                        <MenuItem value="R410A">R410A</MenuItem>
                        <MenuItem value="R134a">R134a</MenuItem>
                        <MenuItem value="R407C">R407C</MenuItem>
                        <MenuItem value="R290">R290 (Propan)</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <TextField
                      fullWidth
                      label="Füllmenge lt. Typenschild (kg)"
                      type="number"
                      inputProps={{ step: '0.01' }}
                      value={formData.kaeltekreislauf?.fuellmenge_typenschild || ''}
                      onChange={(e) => {
                        const typenschild = parseFloat(e.target.value) || 0;
                        const nachfuell = parseFloat(formData.kaeltekreislauf?.nachfuellmenge) || 0;
                        const gesamt = (typenschild + nachfuell).toFixed(2);
                        
                        handleKaeltekreislaufChange('fuellmenge_typenschild', e.target.value);
                        handleKaeltekreislaufChange('gesamtfuellmenge', gesamt);
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Nachfüllmenge (kg)"
                      type="number"
                      inputProps={{ step: '0.01' }}
                      value={formData.kaeltekreislauf?.nachfuellmenge || '0'}
                      onChange={(e) => {
                        const nachfuell = parseFloat(e.target.value) || 0;
                        const typenschild = parseFloat(formData.kaeltekreislauf?.fuellmenge_typenschild) || 0;
                        const gesamt = (typenschild + nachfuell).toFixed(2);
                        
                        handleKaeltekreislaufChange('nachfuellmenge', e.target.value);
                        handleKaeltekreislaufChange('gesamtfuellmenge', gesamt);
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Gesamtfüllmenge (kg)"
                      type="number"
                      value={formData.kaeltekreislauf?.gesamtfuellmenge || '0'}
                      disabled
                      helperText="Automatisch berechnet"
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: '#000',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
              
              {/* === KÄLTEKREISLAUF-DETAILS CARD === */}
              <Card sx={{ backgroundColor: '#e1f5fe', border: '1px solid #0288d1' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#0288d1', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    🔧 Kältekreislauf-Details
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Y-Kältemittelverteiler (Anzahl)"
                      value={formData.kaeltekreislauf?.y_kaeltemittelverteiler || 0}
                      onChange={(e) => handleKaeltekreislaufChange('y_kaeltemittelverteiler', parseInt(e.target.value) || 0)}
                    />
                    
                    <TextField
                      fullWidth
                      type="number"
                      label="Höhendifferenz Innen-/Außengerät (m)"
                      value={formData.kaeltekreislauf?.hoehendifferenz || ''}
                      onChange={(e) => handleKaeltekreislaufChange('hoehendifferenz', e.target.value)}
                    />
                    
                    <TextField
                      fullWidth
                      type="number"
                      label="Leitungslänge (m)"
                      value={formData.kaeltekreislauf?.leitungslaenge || ''}
                      onChange={(e) => handleKaeltekreislaufChange('leitungslaenge', e.target.value)}
                    />
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.kaeltekreislauf?.lotstellen_vorhanden || false}
                            onChange={(e) => handleKaeltekreislaufChange('lotstellen_vorhanden', e.target.checked)}
                            onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleKaeltekreislaufChange('lotstellen_vorhanden', !formData.kaeltekreislauf?.lotstellen_vorhanden))}
                            sx={{
                              color: formData.kaeltekreislauf?.lotstellen_vorhanden ? 'success.main' : 'default',
                              '&.Mui-checked': { color: 'success.main' }
                            }}
                          />
                        }
                        label="Lötstellen vorhanden"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.kaeltekreislauf?.isolierte_leitungen || false}
                            onChange={(e) => handleKaeltekreislaufChange('isolierte_leitungen', e.target.checked)}
                            onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleKaeltekreislaufChange('isolierte_leitungen', !formData.kaeltekreislauf?.isolierte_leitungen))}
                            sx={{
                              color: formData.kaeltekreislauf?.isolierte_leitungen ? 'success.main' : 'default',
                              '&.Mui-checked': { color: 'success.main' }
                            }}
                          />
                        }
                        label="Leitungen isoliert"
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
              
              {/* === DICHTEPRÜFUNG MIT STICKSTOFF 5.0 CARD === */}
              <Card sx={{ backgroundColor: '#fff3e0', border: '1px solid #ff9800' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    🔍 Dichteprüfung mit Stickstoff 5.0
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.kaeltekreislauf?.dichtepruefung_durchgefuehrt || false}
                          onChange={(e) => handleKaeltekreislaufChange('dichtepruefung_durchgefuehrt', e.target.checked)}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleKaeltekreislaufChange('dichtepruefung_durchgefuehrt', !formData.kaeltekreislauf?.dichtepruefung_durchgefuehrt))}
                          sx={{
                            color: formData.kaeltekreislauf?.dichtepruefung_durchgefuehrt ? 'success.main' : 'default',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label="Dichteprüfung durchgeführt"
                    />
                    
                    {formData.kaeltekreislauf?.dichtepruefung_durchgefuehrt && (
                      <>
                        <Divider />
                        
                        <TextField
                          fullWidth
                          label="Prüfdruck (bar)"
                          type="number"
                          value={formData.kaeltekreislauf?.pruefdruck || ''}
                          onChange={(e) => handleKaeltekreislaufChange('pruefdruck', e.target.value)}
                        />
                        
                        <TextField
                          fullWidth
                          label="Prüfzeit (min)"
                          type="number"
                          value={formData.kaeltekreislauf?.pruefzeit || ''}
                          onChange={(e) => handleKaeltekreislaufChange('pruefzeit', e.target.value)}
                        />
                        
                        <TextField
                          fullWidth
                          label="Evakuierungszeit (min)"
                          type="number"
                          value={formData.kaeltekreislauf?.evakuierungszeit || ''}
                          onChange={(e) => handleKaeltekreislaufChange('evakuierungszeit', e.target.value)}
                        />
                        
                        <TextField
                          fullWidth
                          type="number"
                          label="Erzeugtes Vakuum (μ micron)"
                          value={formData.kaeltekreislauf?.vakuum_erzeugt_micron || ''}
                          onChange={(e) => handleKaeltekreislaufChange('vakuum_erzeugt_micron', e.target.value)}
                          placeholder="z.B. 500"
                        />
                        
                        <FormControl fullWidth>
                          <InputLabel>Ergebnis</InputLabel>
                          <Select
                            value={formData.kaeltekreislauf?.anlage_status || 'dicht'}
                            onChange={(e) => handleKaeltekreislaufChange('anlage_status', e.target.value)}
                            label="Ergebnis"
                          >
                            <MenuItem value="dicht">Dicht ✓</MenuItem>
                            <MenuItem value="undicht">Undicht ✗</MenuItem>
                          </Select>
                        </FormControl>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 5: Wartung */}
        <Accordion 
          expanded={expanded === 'panel5'} 
          onChange={handleAccordionChange('panel5')}
          sx={{ 
            mb: 2,
            boxShadow: 3,
            '&:before': { display: 'none' },
            borderRadius: '8px !important'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: '#ff9800',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&:hover': { backgroundColor: '#f57c00' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Build />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>5. Wartung</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* === ZUKÜNFTIGE DICHTEPRÜFUNG CARD === */}
              <Card sx={{ backgroundColor: '#fff3e0', border: '1px solid #ff9800' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#ff9800', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    🔍 Zukünftige Dichteprüfung
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.wartung?.dichtepruefung_erforderlich || false}
                          onChange={(e) => handleWartungChange('dichtepruefung_erforderlich', e.target.checked)}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleWartungChange('dichtepruefung_erforderlich', !formData.wartung?.dichtepruefung_erforderlich))}
                          sx={{
                            color: formData.wartung?.dichtepruefung_erforderlich ? 'warning.main' : 'default',
                            '&.Mui-checked': { color: 'warning.main' }
                          }}
                        />
                      }
                      label="Dichteprüfung erforderlich"
                    />
                    
                    {formData.wartung?.dichtepruefung_erforderlich && (
                      <>
                        <Divider />
                        <FormControl fullWidth>
                          <InputLabel>Prüfintervall</InputLabel>
                          <Select
                            value={formData.wartung?.pruefintervall_frequenz || '12'}
                            onChange={(e) => handleWartungChange('pruefintervall_frequenz', e.target.value)}
                            label="Prüfintervall"
                          >
                            <MenuItem value="12">12 Monate (Standard)</MenuItem>
                            <MenuItem value="6">6 Monate</MenuItem>
                            <MenuItem value="3">3 Monate</MenuItem>
                          </Select>
                        </FormControl>
                      </>
                    )}
                  </Box>
                </CardContent>
              </Card>
              
              {/* === WARTUNGSVERTRAG CARD === */}
              <Card sx={{ backgroundColor: '#e8f5e9', border: '1px solid #4caf50' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#4caf50', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    📝 Wartungsvertrag
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.wartung?.wartungsangebot_gewuenscht || false}
                          onChange={(e) => handleWartungChange('wartungsangebot_gewuenscht', e.target.checked)}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleWartungChange('wartungsangebot_gewuenscht', !formData.wartung?.wartungsangebot_gewuenscht))}
                          sx={{
                            color: formData.wartung?.wartungsangebot_gewuenscht ? 'success.main' : 'default',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label="Wartungsangebot gewünscht"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 7: Funktionstest & Dokumentation */}
        <Accordion 
          expanded={expanded === 'panel7'} 
          onChange={handleAccordionChange('panel7')}
          sx={{ 
            mb: 2,
            boxShadow: 3,
            '&:before': { display: 'none' },
            borderRadius: '8px !important'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: '#1976d2',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&:hover': { backgroundColor: '#1565c0' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>7. Funktionstest & Dokumentation</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* === FUNKTIONSTEST CARD === */}
              <Card sx={{ backgroundColor: '#e3f2fd', border: '1px solid #1976d2' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    ✅ Funktionstest
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.funktionstest?.testbetrieb_kuehlen || false}
                          onChange={(e) => handleFunktionstestChange('testbetrieb_kuehlen', e.target.checked)}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleFunktionstestChange('testbetrieb_kuehlen', !formData.funktionstest?.testbetrieb_kuehlen))}
                          sx={{
                            color: formData.funktionstest?.testbetrieb_kuehlen ? 'success.main' : 'default',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label="Testbetrieb Kühlen durchgeführt"
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.funktionstest?.testbetrieb_heizen || false}
                          onChange={(e) => handleFunktionstestChange('testbetrieb_heizen', e.target.checked)}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleFunktionstestChange('testbetrieb_heizen', !formData.funktionstest?.testbetrieb_heizen))}
                          sx={{
                            color: formData.funktionstest?.testbetrieb_heizen ? 'success.main' : 'default',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label="Testbetrieb Heizen durchgeführt"
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.funktionstest?.kondenswasser_geprueft || false}
                          onChange={(e) => handleFunktionstestChange('kondenswasser_geprueft', e.target.checked)}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleFunktionstestChange('kondenswasser_geprueft', !formData.funktionstest?.kondenswasser_geprueft))}
                          sx={{
                            color: formData.funktionstest?.kondenswasser_geprueft ? 'success.main' : 'default',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label="Kondenswasser geprüft"
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.funktionstest?.inbetriebnahme_durchgefuehrt || false}
                          onChange={(e) => handleFunktionstestChange('inbetriebnahme_durchgefuehrt', e.target.checked)}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleFunktionstestChange('inbetriebnahme_durchgefuehrt', !formData.funktionstest?.inbetriebnahme_durchgefuehrt))}
                          sx={{
                            color: formData.funktionstest?.inbetriebnahme_durchgefuehrt ? 'success.main' : 'default',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label="Inbetriebnahme durchgeführt"
                    />
                  </Box>
                </CardContent>
              </Card>
              
              {/* === DOKUMENTATION & ÜBERGABE CARD === */}
              <Card sx={{ backgroundColor: '#f3e5f5', border: '1px solid #9c27b0' }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#9c27b0', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    📄 Dokumentation & Übergabe
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.funktionstest?.uebergabe_betriebsbuch || false}
                          onChange={(e) => handleFunktionstestChange('uebergabe_betriebsbuch', e.target.checked)}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleFunktionstestChange('uebergabe_betriebsbuch', !formData.funktionstest?.uebergabe_betriebsbuch))}
                          sx={{
                            color: formData.funktionstest?.uebergabe_betriebsbuch ? 'success.main' : 'default',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label="Betriebsbuch übergeben"
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.funktionstest?.einweisung_durchgefuehrt || false}
                          onChange={(e) => handleFunktionstestChange('einweisung_durchgefuehrt', e.target.checked)}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleFunktionstestChange('einweisung_durchgefuehrt', !formData.funktionstest?.einweisung_durchgefuehrt))}
                          sx={{
                            color: formData.funktionstest?.einweisung_durchgefuehrt ? 'success.main' : 'default',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label="Einweisung durchgeführt"
                    />
                    
                    <TextField
                      fullWidth
                      label="Unterwiesene Personen"
                      value={formData.funktionstest?.unterwiesene_personen || ''}
                      onChange={(e) => handleFunktionstestChange('unterwiesene_personen', e.target.value)}
                      placeholder="Namen der eingewiesenen Personen"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 
          expanded={expanded === 'panel5'} 
          onChange={handleAccordionChange('panel5')}
          sx={{ border: '2px solid #2196f3', borderRadius: 1 }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ backgroundColor: '#e3f2fd' }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              5. Kältekreislauf-Details (IBN)
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Y-Kältemittelverteiler (Anzahl)"
                  value={formData.y_kaeltemittelverteiler}
                  onChange={(e) => setFormData({ ...formData, y_kaeltemittelverteiler: parseInt(e.target.value) || 0 })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Gesamtkälteleistung (kW)"
                  value={formData.gesamtkaelteleistung}
                  onChange={(e) => setFormData({ ...formData, gesamtkaelteleistung: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Höhendifferenz Innen-/Außengerät (m)"
                  value={formData.hoehendifferenz}
                  onChange={(e) => setFormData({ ...formData, hoehendifferenz: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Leitungslänge (m)"
                  value={formData.leitungslaenge}
                  onChange={(e) => setFormData({ ...formData, leitungslaenge: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.lotstellen_vorhanden}
                      onChange={(e) => setFormData({ ...formData, lotstellen_vorhanden: e.target.checked })}
                    />
                  }
                  label="Lötstellen vorhanden"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.isolierte_leitungen}
                      onChange={(e) => setFormData({ ...formData, isolierte_leitungen: e.target.checked })}
                    />
                  }
                  label="Isolierte Leitungen"
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Accordion 8: Hinweise, Bilder & Unterschriften */}
        <Accordion 
          expanded={expanded === 'panel8'} 
          onChange={handleAccordionChange('panel8')}
          sx={{ 
            mb: 2,
            boxShadow: 3,
            '&:before': { display: 'none' },
            borderRadius: '8px !important',
            border: '2px solid #4caf50'
          }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMore />}
            sx={{ 
              backgroundColor: '#4caf50',
              color: 'white',
              borderRadius: '8px 8px 0 0',
              '&:hover': { backgroundColor: '#388e3c' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>8. Hinweise, Bilder & Unterschriften</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: { xs: 2, sm: 3 }, backgroundColor: 'white' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              
              {/* 0. MÄNGEL BEI INBETRIEBNAHME - NEU */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Cancel color="error" />
                  Mängel bei Inbetriebnahme
                </Typography>
                
                <FormControl>
                  <FormLabel sx={{ fontWeight: 'bold', mb: 1 }}>Mängel bei Inbetriebnahme festgestellt?</FormLabel>
                  <RadioGroup
                    row
                    value={formData.funktionstest?.maengel_festgestellt || 'nein'}
                    onChange={(e) => {
                      handleFunktionstestChange('maengel_festgestellt', e.target.value);
                      if (e.target.value === 'nein') {
                        handleFunktionstestChange('festgestellte_maengel', '');
                      }
                    }}
                    sx={{ mb: 2 }}
                  >
                    <FormControlLabel 
                      value="nein" 
                      control={<Radio />} 
                      label="Nein" 
                    />
                    <FormControlLabel 
                      value="ja" 
                      control={<Radio />} 
                      label="Ja" 
                    />
                  </RadioGroup>
                </FormControl>
                
                {formData.funktionstest?.maengel_festgestellt === 'ja' && (
                  <Box sx={{ mt: 2 }}>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      ⚠️ Festgestellte Mängel angeben
                    </Alert>
                    <TextField
                      fullWidth
                      multiline
                      rows={5}
                      label="Festgestellte Mängel"
                      value={formData.funktionstest?.festgestellte_maengel || ''}
                      onChange={(e) => handleFunktionstestChange('festgestellte_maengel', e.target.value)}
                      placeholder="Bitte beschreiben Sie die festgestellten Mängel..."
                      required
                      inputRef={panel9FirstFieldRef}
                      sx={{ 
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffebee',
                          fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' },
                          borderColor: '#f44336',
                          '& fieldset': {
                            borderColor: '#f44336',
                            borderWidth: 2
                          },
                          '&:hover fieldset': {
                            borderColor: '#d32f2f',
                            borderWidth: 2
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#f44336',
                            borderWidth: 2
                          }
                        }
                      }}
                    />
                  </Box>
                )}
              </Box>
              
              <Divider />
              
              {/* 1. HINWEISE - Neue Zeile, 100% Breite */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Info color="info" />
                  Hinweise
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  label="Zusätzliche Hinweise oder Bemerkungen"
                  value={formData.hinweise}
                  onChange={(e) => handleChange('hinweise', e.target.value)}
                  placeholder="Z.B. besondere Vorkommnisse, Empfehlungen, durchgeführte Reparaturen..."
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fafafa',
                      fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' }
                    }
                  }}
                />
              </Box>
              
              <Divider />
              
              {/* 2. BILDER/FOTOS - Neue Zeile, 100% Breite */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PhotoCamera color="success" />
                  Fotos (max. 21 Bilder)
                </Typography>
                <ImageUploader
                  images={formData.bilder || []}
                  onImagesSelected={(newImages) => {
                    handleChange('bilder', newImages);
                  }}
                  maxImages={21}
                  locationOptions={[
                    ...formData.innengeraete.map((ig, idx) => 
                      `Innengerät ${idx + 1}${ig.montageort ? ` - ${ig.montageort}` : ''}`
                    ),
                    `Außengerät${formData.aussengeraet?.montageort ? ` - ${formData.aussengeraet.montageort}` : ''}`
                  ]}
                />
              </Box>
              
              <Divider />
              
              {/* 3. UNTERSCHRIFT MONTEUR - Neue Zeile */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Draw color="primary" />
                  Unterschrift Monteur *
                </Typography>
                <Box sx={{ 
                  border: '2px dashed #2196f3', 
                  borderRadius: 2, 
                  p: 3, 
                  textAlign: 'center',
                  backgroundColor: formData.monteur_unterschrift ? '#e3f2fd' : '#fafafa',
                  minHeight: 150
                }}>
                  {formData.monteur_unterschrift ? (
                    <Box>
                      <img 
                        src={formData.monteur_unterschrift} 
                        alt="Monteur Unterschrift" 
                        style={{ maxWidth: '100%', maxHeight: '120px', border: '1px solid #ddd' }}
                      />
                      <Box sx={{ mt: 2 }}>
                        <Button 
                          variant="outlined" 
                          onClick={() => setMonteurSigDialog(true)}
                        >
                          Neu unterschreiben
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Button 
                      variant="contained" 
                      size="large"
                      startIcon={<Draw />}
                      onClick={() => setMonteurSigDialog(true)}
                    >
                      Jetzt unterschreiben
                    </Button>
                  )}
                </Box>
              </Box>
              
              <Divider />
              
              {/* 4. KUNDENZUFRIEDENHEIT - Neue Zeile */}
              <Box>
                <Card sx={{ p: 3, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CheckCircle color="success" />
                    Kundenzufriedenheit
                  </Typography>
                  
                  <Typography variant="body1" gutterBottom fontWeight="medium" sx={{ mb: 2 }}>
                    Wurde die Wartung zu Ihrer Zufriedenheit durchgeführt?
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.kunde_zufrieden === 'ja'}
                          onChange={() => {
                            handleChange('kunde_zufrieden', 'ja');
                            handleChange('kunde_zufrieden_begruendung', '');
                          }}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => {
                            handleChange('kunde_zufrieden', 'ja');
                            handleChange('kunde_zufrieden_begruendung', '');
                          })}
                          inputProps={{ tabIndex: 0 }}
                          sx={{
                            color: 'success.light',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label={<Typography variant="body1"><strong>Ja</strong>, ich bin zufrieden</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.kunde_zufrieden === 'nein'}
                          onChange={() => handleChange('kunde_zufrieden', 'nein')}
                          onKeyDown={(e) => handleCheckboxKeyDown(e, () => handleChange('kunde_zufrieden', 'nein'))}
                          inputProps={{ tabIndex: 0 }}
                          sx={{
                            color: 'error.light',
                            '&.Mui-checked': { color: 'error.main' }
                          }}
                        />
                      }
                      label={<Typography variant="body1"><strong>Nein</strong>, ich bin nicht zufrieden</Typography>}
                    />
                  </Box>
                  
                  {/* Begründung nur bei "nein" */}
                  {formData.kunde_zufrieden === 'nein' && (
                    <Box sx={{ mt: 3 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Begründung (Pflichtfeld)"
                        value={formData.kunde_zufrieden_begruendung || ''}
                        onChange={(e) => handleChange('kunde_zufrieden_begruendung', e.target.value)}
                        placeholder="Bitte geben Sie an, warum Sie nicht zufrieden sind..."
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#fff3cd',
                            borderColor: '#ffc107'
                          }
                        }}
                      />
                    </Box>
                  )}
                </Card>
              </Box>
              
              <Divider />
              
              {/* 5. NAME KUNDE (Textfeld) - Neue Zeile */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                  Name des Unterzeichners (optional)
                </Typography>
                <TextField
                  fullWidth
                  label="Name des Kunden (falls abweichend)"
                  value={formData.kunde_name_unterschrift || ''}
                  onChange={(e) => handleChange('kunde_name_unterschrift', capitalizeFirst(e.target.value))}
                  placeholder="Z.B. wenn eine andere Person unterschreibt"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#fafafa'
                    }
                  }}
                />
              </Box>
              
              <Divider />
              
              {/* EMAIL-EMPFÄNGER */}
              <Box sx={{ p: 3, border: '2px solid #2196f3', borderRadius: 2, backgroundColor: '#e3f2fd' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  📧 Email-Empfänger
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  label="Kunden-Email"
                  value={formData.kunde_email || ''}
                  onChange={(e) => handleChange('kunde_email', e.target.value)}
                  placeholder="kunde@example.com"
                  helperText="Das Protokoll wird an diese Email-Adresse gesendet"
                  sx={{ 
                    minWidth: 200,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff'
                    }
                  }}
                />
              </Box>
              
              <Divider />
              
              {/* 6. UNTERSCHRIFT KUNDE - Neue Zeile */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Draw color="success" />
                  Unterschrift Kunde *
                </Typography>
                <Box sx={{ 
                  border: '2px dashed #4caf50', 
                  borderRadius: 2, 
                  p: 3, 
                  textAlign: 'center',
                  backgroundColor: formData.kunde_unterschrift ? '#e8f5e9' : '#fafafa',
                  minHeight: 150
                }}>
                  {formData.kunde_unterschrift ? (
                    <Box>
                      <img 
                        src={formData.kunde_unterschrift} 
                        alt="Kunde Unterschrift" 
                        style={{ maxWidth: '100%', maxHeight: '120px', border: '1px solid #ddd' }}
                      />
                      <Box sx={{ mt: 2 }}>
                        <Button 
                          variant="outlined"
                          color="success"
                          onClick={() => setKundeSigDialog(true)}
                        >
                          Neu unterschreiben
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Button 
                      variant="contained" 
                      color="success"
                      size="large"
                      startIcon={<Draw />}
                      onClick={() => setKundeSigDialog(true)}
                    >
                      Jetzt unterschreiben
                    </Button>
                  )}
                </Box>
              </Box>
              
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* MONTEUR UNTERSCHRIFT POPUP */}
        <Dialog 
          open={monteurSigDialog} 
          onClose={() => {
            // Nicht schließen wenn keine Unterschrift
            if (!formData.monteur_unterschrift) {
              alert('Bitte unterschreiben Sie oder brechen Sie ab');
              return;
            }
            setMonteurSigDialog(false);
          }}
          maxWidth="md"
          fullWidth
          fullScreen={window.innerWidth < 600}
        >
          <DialogTitle>Monteur Unterschrift</DialogTitle>
          <DialogContent sx={{ minHeight: 400, p: 3 }}>
            <SignatureCanvas
              value={formData.monteur_unterschrift}
              onChange={(signature) => {
                console.log('Monteur signature changed:', signature ? 'YES' : 'NO');
                handleChange('monteur_unterschrift', signature);
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => {
                handleChange('monteur_unterschrift', '');
                setMonteurSigDialog(false);
              }}
              variant="outlined"
            >
              Abbrechen
            </Button>
            <Button 
              variant="contained" 
              onClick={() => {
                if (!formData.monteur_unterschrift) {
                  alert('Bitte unterschreiben Sie erst!');
                  return;
                }
                console.log('Saving monteur signature:', formData.monteur_unterschrift);
                setMonteurSigDialog(false);
              }}
              disabled={!formData.monteur_unterschrift}
            >
              Speichern
            </Button>
          </DialogActions>
        </Dialog>

        {/* KUNDE UNTERSCHRIFT POPUP */}
        <Dialog 
          open={kundeSigDialog} 
          onClose={() => {
            if (!formData.kunde_unterschrift) {
              alert('Bitte unterschreiben Sie oder brechen Sie ab');
              return;
            }
            setKundeSigDialog(false);
          }}
          maxWidth="md"
          fullWidth
          fullScreen={window.innerWidth < 600}
        >
          <DialogTitle>Kunde Unterschrift</DialogTitle>
          <DialogContent sx={{ minHeight: 400, p: 3 }}>
            <SignatureCanvas
              value={formData.kunde_unterschrift}
              onChange={(signature) => {
                console.log('Kunde signature changed:', signature ? 'YES' : 'NO');
                handleChange('kunde_unterschrift', signature);
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={() => {
                handleChange('kunde_unterschrift', '');
                setKundeSigDialog(false);
              }}
              variant="outlined"
            >
              Abbrechen
            </Button>
            <Button 
              variant="contained"
              color="success"
              onClick={() => {
                if (!formData.kunde_unterschrift) {
                  alert('Bitte unterschreiben Sie erst!');
                  return;
                }
                console.log('Saving kunde signature:', formData.kunde_unterschrift);
                setKundeSigDialog(false);
              }}
              disabled={!formData.kunde_unterschrift}
            >
              Speichern
            </Button>
          </DialogActions>
        </Dialog>

        {/* Action Buttons */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/protocol/ibn-klima-list')}
            disabled={loading}
          >
            ABBRECHEN
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<Visibility />}
            onClick={handlePreview}
            disabled={loading}
          >
            PDF VORSCHAU
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Save />}
            onClick={handleSaveDraft}
            disabled={loading}
          >
            ALS ENTWURF SPEICHERN
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={loading ? <CircularProgress size={20} /> : <Send />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'WIRD VERSENDET...' : 'SPEICHERN & VERSENDEN'}
          </Button>
        </Box>
      </Container>

      {/* Auto-Save Wiederherstellungs-Dialog */}
      <Dialog 
        open={showRestoreDialog} 
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#1976d2', color: 'white' }}>
          💾 Begonnenes Formular gefunden
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Es wurde ein automatisch gespeichertes Formular gefunden. Möchten Sie Ihre Arbeit fortsetzen?
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Das Formular wurde automatisch gespeichert, als Sie das letzte Mal daran gearbeitet haben.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDiscardAutoSave}
            color="error"
            variant="outlined"
          >
            Verwerfen & neu beginnen
          </Button>
          <Button 
            onClick={handleRestoreAutoSave}
            color="success"
            variant="contained"
            autoFocus
          >
            Wiederherstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
